import React, { useCallback, useContext, useEffect, useState } from 'react'
import NewWindow from 'react-new-window'
import { LandingPageDto } from '@/api/Clients/Types/LandingPageDto'
import { PermissionsEnum } from '@/api/Identity/Types/PermissionsEnum'
import { Dialog } from '@/components/ui/dialog'
import { Body1 } from '@/components/ui/typography'
import { useTradingViewPlanExpirationDays, useTradingViewPlans } from '@/hooks/useTradingViewPlans'
import { LeadsApiClient } from '@/utils/api/leadsApi'
import { getClientAreaUrl } from '@/utils/dom'
import { hasPermission } from '@/utils/permissionsValidator'
import { sleep } from '@/utils/sleep'
import {
  ClientDetailedDto,
  ClientPlatformLimitDto,
  isClientDetailedActivatedStatus,
  UpdateClientPlatformLimitDto,
} from 'api/Clients/Types/ClientDetailedDto'
import { POIDocumentType } from 'api/Clients/Types/DocumentType'
import { TruliooVerification } from 'api/Clients/Types/TruliooVerification'
import { UpdateClientStatus } from 'api/Clients/Types/UpdateClientStatus'
import { NameDto } from 'api/Common/Types'
import { DocumentRequestType } from 'api/Documents/Types/DocumentRequests'
import { TokenStorage } from 'api/tokenStorage'
import { BookTypeID, BookTypeIdToFriendlyName } from 'api/Trading-Accounts/Types/BookType'
import { ApiResponse } from 'App'
import ConfirmationModal from 'components/ConfirmationModal/ConfirmationModal'
import { Modal } from 'components/CustomModal'
import { Spinner } from 'components/Spinner'
import { errorToast, infoToast, successToast, ToastContext } from 'components/Toast/Toast'
import { useAppDispatch, useAppSelector } from 'store'
import {
  fetchAMLCategoriesTypes,
  fetchClientBusinessNatureTypeAction,
  fetchClientClassificationTypeAction,
  fetchClientDocumentTypeAction,
  fetchClientEducationAction,
  fetchClientGenderTypeAction,
  fetchClientLanguagesAction,
  fetchClientLeverageTypeAction,
  fetchClientMarketDataClassificationTypeAction,
  fetchClientNciTypeAction,
  fetchClientSalesStatusAction,
  fetchClientTruliooDocVStatusAction,
  fetchClientTruliooEidStatusAction,
  fetchClientTruliooWatchlistStatusAction,
  fetchClientTypesAction,
  fetchClientWorldCheckStatusAction,
  fetchComplianceVulnerabilityGroupsAction,
  fetchPossibleClientDuplicate,
  fetchPossibleClientRelation,
  fetchTaxAvailabilityStatusAction,
  fetchTickmillProductsAction,
} from 'store/clients/clients.actions'
import {
  fetchAdministrationUsersAction,
  fetchCountriesAction,
  fetchTeamsAction,
  fetchTickmillCompaniesAction,
} from 'store/identity/identity.actions'
import { fetchIntroducingBrokerByTickmillCompany } from 'store/partnerships/partnerships.actions'
import { fetchBooksTypeAction } from 'store/trading-accounts/trading-accounts.actions'
import { ResponseError, useApiClient } from 'utils/api/ApiClient'
import { ClientAreaApiClient } from 'utils/api/clientAreaApi'
import { ClientsApiClient } from 'utils/api/clientsApi'
import { HubSpotApiClient } from 'utils/api/hubSpotApi'
import { IdentityApiClient } from 'utils/api/identityApi'
import { PartnershipsApiClient } from 'utils/api/partnershipsApi'
import { TicketsApiClient } from 'utils/api/ticketsApi'
import { TradingAccountsApiClient } from 'utils/api/tradingAccountsApi'
import { useFetchOne } from 'utils/api/useFetch'
import { WalletsApiClient } from 'utils/api/walletsApiClient'
import { WorldCheckApiClient } from 'utils/api/worldCheckApi'
import { AuthSessionContext } from 'utils/AuthContext'
import { getPrimary } from 'utils/getPrimary'
import { isEmpty } from 'utils/is-empty'

import ClientAreaAuditLogModal from '../../../../../components/ClientAreaAuditLogModal/ClientAreaAuditLogModal'
import ErrorModal from '../../../../../components/ConfirmationModal/ErrorModal'
import ClientAreaAccessLogsModal from '../ClientAreaAccessLogsModal'
import { ClientAssignAffiliateModal } from '../ClientAssignAffiliateModal'
import { ClientDetailsAMLDetailedHistory } from '../ClientDetailsAMLDetailedHistory'
import { ClientDetailsAMLScore } from '../ClientDetailsAMLScore'
import { DocumentPreviewModal } from '../ClientDetailsDocument/DocumentPreviewModal'
import ClientDetailsKYC from '../ClientDetailsKYC'
import ClientDetailsTruliooWindow from '../ClientDetailsTruliooWindow'
import { ClientDetailsNCIHistory } from '../ClientNCIHistory/ClientDetailsNCIHistory'
import { ClientTwoFactorAuthMethodFormModal } from '../ClientTwoFactorAuthMethodFormModal'
import { ClientDetailsWorldCheckHistory } from '../ClientWorldCheck/ClientDetailsWorldCheckHistory'
import { ClientDetailsWorldCheckModal } from '../ClientWorldCheck/ClientDetailsWorldCheckModal'
import { ClientDetailsDashboardForm } from './ClientDetailsDashboardForm'

interface Props {
  data: ClientDetailedDto

  forceRefresh(): void

  updateClientStatus(values: UpdateClientStatus): void
}

const ClientDetailsDashboardPage: React.FC<Props> = ({
  data,
  forceRefresh,
  updateClientStatus,
}) => {
  const hubSpotApiClient = useApiClient(HubSpotApiClient)
  const clientsApiClient = useApiClient(ClientsApiClient)
  const leadsApiClient = useApiClient(LeadsApiClient)
  const identityApiClient = useApiClient(IdentityApiClient)
  const worldCheckApiClient = useApiClient(WorldCheckApiClient)
  const ticketsApiClient = useApiClient(TicketsApiClient)
  const walletsApiClient = useApiClient(WalletsApiClient)
  const tradingAccountsApiClient = useApiClient(TradingAccountsApiClient)
  const partnershipsApiClient = useApiClient(PartnershipsApiClient)
  const clientAreaApiClient = useApiClient(ClientAreaApiClient)
  const individualWorldCheckScreeningTypeId = 1

  const [preferredCurrency, setPreferredCurrency] = useState<NameDto<string>[]>([])
  const [displayCurrency, setDisplayCurrency] = useState<NameDto<string>[]>([])
  const [loading, setLoading] = useState<boolean>(false)
  const [show2FA, setShow2FA] = useState<boolean>(false)

  const [isFormLoading, setFormLoading] = useState<boolean>(false)
  const [accessLogModal, setAccessLogModal] = useState<boolean>(false)
  const [amlDetailsModal, setAmlDetailsModal] = useState<boolean>(false)
  const [amlDetailedModal, setAmlDetailedModal] = useState<boolean>(false)

  const [auditLogVisible, setAuditLogVisible] = useState<boolean>(false)
  const [newWindow, setOpenNewWindow] = useState<boolean>(false)
  const [documentPreviewModal, setDocumentPreviewModal] = useState<string | null>(null)
  const [verificationModal, setVerificationModal] = useState<boolean>(false)
  const [nciHistoryModal, setNCIHistoryModal] = useState<{
    visible: boolean
    showAddForm?: boolean
  }>({
    visible: false,
  })
  const [downgradeRetailErrorModal, setDowngradeRetailErrorModal] = useState<boolean>(false)
  const [reassignIBModal, setReassignIBModal] = useState<ClientDetailedDto>()

  const [landingPages, setLandingPages] = useState<LandingPageDto[]>([])
  const [accountLimits, setAccountLimts] = useState<ClientPlatformLimitDto[]>([])
  const [affiliateModal, setAffiliateModal] = useState<boolean>(false)
  const [forceLogoutModal, setForceLogoutModal] = useState<boolean>(false)

  const { data: tradingViewPlans } = useTradingViewPlans()
  const { data: tradingViewPlanExpirationDays } = useTradingViewPlanExpirationDays()

  const [worldCheckHistoryModal, setWorldCheckHistoryModal] = useState<boolean>(false)
  const [isConfirmationActivateClientModalOpen, setConfirmationActivateClientModalOpen] =
    useState<boolean>(false)
  const [isChangeBookTypeModalConfirmation, setIsChangeBookTypeModalConfirmation] = useState<{
    visible: boolean
    oldBookType?: BookTypeID
    newBookType?: BookTypeID
    values?: ClientDetailedDto
  }>({ visible: false })

  const [auth] = useContext(AuthSessionContext)

  const dispatch = useAppDispatch()
  const permissions = auth?.permissions || []
  const clientType = useAppSelector((state) => state.clients.types)
  const gender = useAppSelector((state) => state.clients.gender)
  const nciType = useAppSelector((state) => state.clients.nciType)
  const education = useAppSelector((state) => state.clients.education)
  const documentType = useAppSelector((state) => state.clients.documentType)
  const businessNature = useAppSelector((state) => state.clients.businessNature)
  const tickmillCompanies = useAppSelector((state) => state.clients.tickmillCompanies)
  const language = useAppSelector((state) => state.clients.language)
  const classification = useAppSelector((state) => state.clients.classification)
  const marketDataClassification = useAppSelector((state) => state.clients.marketDataClassification)
  const bookType = useAppSelector((state) => state.tradingAccounts.bookType)
  const countries = useAppSelector((state) => state.identity.countries)
  const users = useAppSelector((state) => state.identity.administrationUsers)
  const truliooEidStatuses = useAppSelector((state) => state.clients.truliooEidStatus)
  const truliooWatchlistStatuses = useAppSelector((state) => state.clients.truliooWatchlistStatus)
  const truliooDocVStatuses = useAppSelector((state) => state.clients.truliooDocVStatus)
  const worldCheckStatuses = useAppSelector((state) => state.clients.worldCheckStatus)
  const salesStatuses = useAppSelector((state) => state.clients.salesStatus)
  const taxAvailability = useAppSelector((state) => state.clients.taxAvailability)
  const tickmillProducts = useAppSelector((state) => state.clients.tickmillProducts)
  const complianceVulnerabilityGroups = useAppSelector(
    (state) => state.clients.complianceVulnerabilityGroups
  )
  const amlCategories = useAppSelector((state) => state.clients.amlCategories)

  const introducingBrokersByCompany = useAppSelector(
    (state) => state.partnerships.introducingBrokersByCompany[data.tickmillCompany.id] || []
  )
  const teams = useAppSelector((state) => state.identity.teams)
  const leverageType = useAppSelector((state) => state.clients.leverageType)
  const dateFormatType = auth?.user.dateFormatType.name

  const setToast = useContext(ToastContext)
  const [worldCheckModal, setWorldCheckModal] = useState<{
    visible: boolean
    enableOngoingScreening: boolean
  }>({
    visible: false,
    enableOngoingScreening: false,
  })
  const [twoFactorModal, setTwoFactorModal] = useState(false)

  const [worldCheckClientDetailsModal, setWorldCheckClientDetailsModal] = useState<{
    visible: boolean
    caseId: string | undefined
  }>({
    visible: false,
    caseId: undefined,
  })

  const getPreferredCurrencies = async () => {
    if (data.id) {
      // allow only wallet type FX/CFD
      const wallets = await walletsApiClient.getWalletConfig(data.id, {
        ignoreExistingWallets: true,
      })
      setPreferredCurrency(wallets)
    }
  }

  const getDisplayCurrencies = async () => {
    const wallets = await tradingAccountsApiClient.getDisplayCurrencies()
    setDisplayCurrency(wallets)
  }

  const fetchLandingPages = async () => {
    const resp = await clientsApiClient.getClientLandingPages(data.id)
    const mappedResponse = resp.map((x) => x.landingPages).flat()
    setLandingPages(mappedResponse)
  }

  const fetchPlatformLimits = async () => {
    const resp = await tradingAccountsApiClient.getTradingAccountLimits(data.id)
    setAccountLimts(resp)
  }

  const updatePlatformLimits = async (platformsData: UpdateClientPlatformLimitDto) => {
    try {
      await tradingAccountsApiClient.updateTradingAccountLimits(data.id, platformsData)
      await fetchPlatformLimits()
      setToast(successToast('Platform limits updated.'))
    } catch (error) {
      console.error('Error updating platform limits.:', error)
      setToast(errorToast('Failed to update platform limits.'))
    }
  }

  const checkIf2FAForCountryIsEnabled = async () => {
    const addressDto = data.addresses.find((x) => x.isPrimary)
    if (addressDto?.country?.id) {
      const resp = await clientsApiClient.getCountry2FA({
        displayAllCountries: true,
        showHiddenCountries: true,
        countryId: addressDto.country?.id,
      })
      setShow2FA(!!resp[0]?.twoFactorAuthVerificationProviders.length)
    }
  }

  const fetchUsers = useCallback(() => {
    return identityApiClient.getAdministrationUserStatistics(data.id)
  }, [data.id]) // eslint-disable-line react-hooks/exhaustive-deps
  const { data: userStatistics, isLoading } = useFetchOne(fetchUsers)

  useEffect(() => {
    if (isEmpty(clientType)) {
      dispatch(fetchClientTypesAction(clientsApiClient))
    }
    if (isEmpty(gender)) {
      dispatch(fetchClientGenderTypeAction(clientsApiClient))
    }
    if (isEmpty(nciType)) {
      dispatch(fetchClientNciTypeAction(clientsApiClient))
    }
    if (isEmpty(education)) {
      dispatch(fetchClientEducationAction(clientsApiClient))
    }
    if (isEmpty(documentType)) {
      dispatch(fetchClientDocumentTypeAction(clientsApiClient))
    }
    if (isEmpty(businessNature)) {
      dispatch(fetchClientBusinessNatureTypeAction(clientsApiClient))
    }
    if (isEmpty(tickmillCompanies)) {
      dispatch(fetchTickmillCompaniesAction(identityApiClient))
    }
    if (isEmpty(language)) {
      dispatch(fetchClientLanguagesAction(clientsApiClient))
    }
    if (isEmpty(classification)) {
      dispatch(fetchClientClassificationTypeAction(clientsApiClient))
    }
    if (isEmpty(marketDataClassification)) {
      dispatch(fetchClientMarketDataClassificationTypeAction(clientsApiClient))
    }
    if (isEmpty(bookType)) {
      dispatch(fetchBooksTypeAction(tradingAccountsApiClient))
    }
    if (isEmpty(countries)) {
      dispatch(fetchCountriesAction(identityApiClient))
    }
    if (isEmpty(worldCheckStatuses)) {
      dispatch(fetchClientWorldCheckStatusAction(clientsApiClient))
    }
    if (isEmpty(truliooEidStatuses)) {
      dispatch(fetchClientTruliooEidStatusAction(clientsApiClient))
    }
    if (isEmpty(truliooWatchlistStatuses)) {
      dispatch(fetchClientTruliooWatchlistStatusAction(clientsApiClient))
    }
    if (isEmpty(truliooDocVStatuses)) {
      dispatch(fetchClientTruliooDocVStatusAction(clientsApiClient))
    }
    if (isEmpty(salesStatuses)) {
      dispatch(fetchClientSalesStatusAction(clientsApiClient))
    }
    if (isEmpty(teams)) {
      dispatch(fetchTeamsAction(identityApiClient))
    }
    if (isEmpty(taxAvailability)) {
      dispatch(fetchTaxAvailabilityStatusAction(clientsApiClient))
    }
    if (isEmpty(tickmillProducts)) {
      dispatch(fetchTickmillProductsAction(clientsApiClient))
    }
    if (isEmpty(complianceVulnerabilityGroups)) {
      dispatch(fetchComplianceVulnerabilityGroupsAction(clientsApiClient))
    }
    if (isEmpty(amlCategories)) {
      dispatch(fetchAMLCategoriesTypes(clientsApiClient))
    }
    if (isEmpty(landingPages)) {
      fetchLandingPages().then(() => {})
    }
    if (isEmpty(accountLimits)) fetchPlatformLimits()
    checkIf2FAForCountryIsEnabled().then(() => {})

    dispatch(fetchAdministrationUsersAction(identityApiClient))
    dispatch(fetchClientLeverageTypeAction(data.id, clientsApiClient))
    // TODO store in within the store under proper tickmillCompany.
    dispatch(fetchPossibleClientDuplicate(data.id, clientsApiClient))
    dispatch(fetchPossibleClientRelation(data.id, clientsApiClient))
    getPreferredCurrencies()
    getDisplayCurrencies()
    getIntroducingBrokersByTickmillCompany()
    const email = getPrimary(data.emails).address
    checkHubSpotConflict(email)
  }, [data.id]) // eslint-disable-line react-hooks/exhaustive-deps

  const updateClient = async (values: ClientDetailedDto, ignoreCheck = false) => {
    setLoading(true)
    try {
      const { companyDetails, ...individualDetailsPayload } = values
      await clientsApiClient.updateClientIndividualDetails(individualDetailsPayload, ignoreCheck)

      setReassignIBModal(undefined)
      await sleep(3000)
      setToast(successToast('Client updated successfully.'))
      forceRefresh()
    } catch (e) {
      if (
        ((e as ResponseError).response as ApiResponse).response?.data?.code ===
        'client_has_open_trades'
      ) {
        setDowngradeRetailErrorModal(true)
      } else if (
        ((e as ResponseError).response as ApiResponse).response?.data?.code ===
        'introducing_broker_already_in_use'
      ) {
        setReassignIBModal(values)
      } else {
        throw e
      }
    } finally {
      setLoading(false)
    }
  }

  const showConfirmationBookType = async (
    values: ClientDetailedDto,
    oldBookType: BookTypeID,
    newBookType: BookTypeID
  ) => {
    if (newBookType === BookTypeID.A || newBookType === BookTypeID.B) {
      return setIsChangeBookTypeModalConfirmation({
        visible: true,
        oldBookType,
        newBookType,
        values,
      })
    }
    return updateClient(values)
  }

  const onSubmit = async (values: ClientDetailedDto) => {
    if (
      data.defaultTradingAccountBook &&
      values.defaultTradingAccountBook &&
      data.defaultTradingAccountBook?.id !== values.defaultTradingAccountBook?.id
    ) {
      return await showConfirmationBookType(
        values,
        data.defaultTradingAccountBook?.id,
        values.defaultTradingAccountBook?.id
      )
    }
    return updateClient(values)
  }

  const getIntroducingBrokersByTickmillCompany = async () => {
    await dispatch(
      fetchIntroducingBrokerByTickmillCompany(partnershipsApiClient, data.tickmillCompany.id)
    )
  }

  const setMessage = (msg: string) => {
    setToast(infoToast(msg))
  }

  const onWorldCheckResolve = () => {
    setToast(successToast('WorldCheck status updated.'))
  }

  const onWorldCheckFailure = () => {
    forceRefresh()
  }

  const createLead = async () => {
    await leadsApiClient.createLeadFromClient(data.id)
    setToast(successToast('Lead created successfully.'))
  }

  const onWorldCheckNoRecordsFoundResolve = async () => {
    setWorldCheckModal({ visible: false, enableOngoingScreening: false })
    forceRefresh()
  }

  const resetFailedLogins = async () => {
    await identityApiClient.resetClientFailedLogins(data.id)
    setToast(successToast('Client failed logins are now reset.'))
    forceRefresh()
  }

  const resetForgotPasswordAttempts = async () => {
    await identityApiClient.resetClientForgotPasswordAttempts(data.id)
    setToast(successToast('Client password attempts are now reset.'))
    forceRefresh()
  }

  const handleClientDetailsTruliooWindowFormSubmit = async (
    truliooVerification: TruliooVerification
  ) => {
    setFormLoading(true)
    setOpenNewWindow(false)
    await clientsApiClient.postToTruliooVerification(data.id, truliooVerification)
    setToast(successToast('Identity verification completed.'))
    forceRefresh()
    setFormLoading(false)
  }

  const handleIdentityVerificationWindowOpen = async () => {
    setOpenNewWindow(true)
    try {
      await clientsApiClient.getTruliooFields(data.id)
    } catch (error) {
      console.error(error)
    }
  }

  const checkHubSpotConflict = async (email: string) => {
    const conflict = await hubSpotApiClient.checkHubspotConflict(email)
    if (conflict) {
      return setToast({
        type: 'warning',
        title: 'Please resolve the conflict',
        requireInteraction: true,
        subtitle: `The email address ${email} causes a conflict in HubSpot as it is used by record ${conflict.firstname} ${conflict.lastname}. Please click this notification to resolve the conflict.`,
        url: conflict.contactUrl,
      })
    }
  }

  const handleWorldCheckScreeningModelOpen = (caseId: string) => {
    setWorldCheckClientDetailsModal({ visible: true, caseId })
  }

  const handleWorldCheckScreeningModelClose = () => {
    setWorldCheckClientDetailsModal({ visible: false, caseId: undefined })
  }

  const handleClientDetailsTruliooWindowClose = () => {
    setFormLoading(false)
    setOpenNewWindow(false)
  }

  const handleConfirmationActivateClientModalOpen = () => {
    setConfirmationActivateClientModalOpen(true)
  }

  const handleConfirmationActivateClientModalClose = () => {
    setConfirmationActivateClientModalOpen(false)
  }

  const handleActivateClient = async (values: UpdateClientStatus) => {
    if (isClientDetailedActivatedStatus(values.status.id)) {
      handleConfirmationActivateClientModalOpen()
    } else {
      await updateClientStatus(values)
      setConfirmationActivateClientModalOpen(false)
    }
  }

  const verifyClient = async () => {
    const updateStatusData: UpdateClientStatus = {
      id: data.id,
      status: {
        id: 4,
        name: 'Activated',
      },
    }
    await updateClientStatus(updateStatusData)
    setConfirmationActivateClientModalOpen(false)
  }

  const reCalculateNCI = async () => {
    await clientsApiClient.reCalculateClientNCI(data.id)
    setToast(successToast('NCI generated successfully.'))
    forceRefresh()
  }

  const syncHubspot = async () => {
    await hubSpotApiClient.syncHubSpotForClients({ clientIds: [data.id] })
    setToast(successToast('HubSpot synced.'))
    forceRefresh()
  }

  const sendClientLogoutSession = async () => {
    const resp = await identityApiClient.forceLogoutClientSessions(data.id)
    setToast(successToast(resp))
    forceRefresh()
  }

  const handleCALogin = () => {
    const token = TokenStorage.getAccessToken()
    if (!token) {
      setToast(errorToast('No access token found.'))
      return
    }
    const caUrl = getClientAreaUrl()
    const params = new URLSearchParams({
      token,
      clientId: data.id,
    })
    window.open(caUrl + '/auth?' + params.toString(), '_blank')
  }

  const sendClientRequest = async (type: POIDocumentType) => {
    await clientAreaApiClient.updateClientPOIRequest(data.id, type)
    setToast(successToast('Email sent.'))
    forceRefresh()
  }

  const sendClientOtherRequest = async (type: DocumentRequestType) => {
    await clientAreaApiClient.sendOtherRequest({ clientId: data.id, requestType: type })
    setToast(successToast('Email sent.'))
    forceRefresh()
  }

  if (isLoading) {
    return <Spinner />
  }

  return (
    <React.Fragment>
      {twoFactorModal && (
        <ClientTwoFactorAuthMethodFormModal
          updateClient={forceRefresh}
          clientData={data}
          onClose={() => setTwoFactorModal(false)}
        />
      )}
      {affiliateModal && (
        <ClientAssignAffiliateModal
          clientId={data.id}
          updateClient={forceRefresh}
          onClose={() => setAffiliateModal(false)}
        />
      )}
      {worldCheckModal.visible && (
        <Modal
          visible={worldCheckModal.visible}
          width={1400}
          overflow='auto'
          onClose={() => setWorldCheckModal({ visible: false, enableOngoingScreening: false })}
          draggable
        >
          <ClientDetailsWorldCheckModal
            clientId={data.id}
            typeId={individualWorldCheckScreeningTypeId}
            worldCheckApiClient={worldCheckApiClient}
            ticketsApiClient={ticketsApiClient}
            enableOngoingScreening={worldCheckModal.enableOngoingScreening}
            onResolve={() => onWorldCheckResolve()}
            onFailure={() => onWorldCheckFailure()}
            onWorldCheckNoRecordsFoundResolve={onWorldCheckNoRecordsFoundResolve}
          />
        </Modal>
      )}
      {newWindow && (
        <NewWindow
          onUnload={() => setOpenNewWindow(false)}
          copyStyles
          features={{ left: 200, top: 200, width: 800, height: 800 }}
        >
          <ClientDetailsTruliooWindow
            clientId={data.id}
            onSubmit={handleClientDetailsTruliooWindowFormSubmit}
            closeModal={handleClientDetailsTruliooWindowClose}
          />
        </NewWindow>
      )}
      {!!accessLogModal && (
        <Dialog open={!!accessLogModal} onOpenChange={(open) => !open && setAccessLogModal(false)}>
          <ClientAreaAccessLogsModal clientId={data.id} />
        </Dialog>
      )}
      {amlDetailsModal && (
        <Modal
          visible={amlDetailsModal}
          width={1200}
          overflow='auto'
          onClose={() => {
            setAmlDetailsModal(false)
            forceRefresh()
          }}
        >
          <ClientDetailsAMLScore
            data={data}
            onHistoryOpen={() => {
              setAmlDetailsModal(false)
              setAmlDetailedModal(true)
            }}
          />
        </Modal>
      )}
      {amlDetailedModal && (
        <Modal
          visible={amlDetailedModal}
          width={1200}
          overflow='auto'
          onClose={() => {
            setAmlDetailsModal(false)
            setAmlDetailedModal(false)
            forceRefresh()
          }}
        >
          <ClientDetailsAMLDetailedHistory data={data} />
        </Modal>
      )}
      {auditLogVisible && (
        <ClientAreaAuditLogModal
          isVisible={auditLogVisible}
          onClose={() => setAuditLogVisible(false)}
          clientId={data.id}
        />
      )}
      {!!documentPreviewModal && (
        <DocumentPreviewModal
          document={{ id: documentPreviewModal }}
          onClose={() => setDocumentPreviewModal(null)}
        />
      )}
      {verificationModal && (
        <Modal
          visible={verificationModal}
          width={1200}
          overflow='auto'
          onClose={() => setVerificationModal(false)}
        >
          <ClientDetailsKYC data={data} />
        </Modal>
      )}
      {nciHistoryModal.visible && (
        <Modal
          visible={nciHistoryModal.visible}
          width={1200}
          overflow='auto'
          onClose={() => setNCIHistoryModal({ visible: false })}
        >
          <ClientDetailsNCIHistory
            clientsApiClient={clientsApiClient}
            countries={data.nationalities}
            showAddForm={nciHistoryModal.showAddForm}
            clientId={data.id}
            tickmillCompanyId={data.tickmillCompany.id}
            closeModal={() => setNCIHistoryModal({ visible: false })}
          />
        </Modal>
      )}
      {worldCheckHistoryModal && (
        <Modal
          draggable
          noDragZone='table'
          visible={worldCheckHistoryModal}
          width={1200}
          overflow='auto'
          onClose={() => setWorldCheckHistoryModal(false)}
        >
          <ClientDetailsWorldCheckHistory
            clientId={data.id}
            typeId={individualWorldCheckScreeningTypeId}
            worldCheckApiClient={worldCheckApiClient}
            onWorldCheckScreeningModelOpen={handleWorldCheckScreeningModelOpen}
          />
        </Modal>
      )}
      {worldCheckClientDetailsModal.visible && (
        <Modal
          draggable
          noDragZone='table'
          visible={worldCheckClientDetailsModal.visible}
          width={1400}
          overflow='auto'
          onClose={handleWorldCheckScreeningModelClose}
        >
          <ClientDetailsWorldCheckModal
            clientId={data.id}
            typeId={individualWorldCheckScreeningTypeId}
            caseId={worldCheckClientDetailsModal.caseId}
            worldCheckApiClient={worldCheckApiClient}
            ticketsApiClient={ticketsApiClient}
            isFromHistory
            onWorldCheckNoRecordsFoundResolve={onWorldCheckNoRecordsFoundResolve}
            onResolve={() => onWorldCheckResolve()}
            onFailure={() => onWorldCheckFailure()}
          />
        </Modal>
      )}
      {isChangeBookTypeModalConfirmation.visible && isChangeBookTypeModalConfirmation.values && (
        <ConfirmationModal
          renderTitle={
            <Body1>
              {`You have requested to change the Trading Account Group between different book categories 
                            (From ${
                              isChangeBookTypeModalConfirmation.oldBookType
                                ? BookTypeIdToFriendlyName[
                                    isChangeBookTypeModalConfirmation.oldBookType
                                  ]
                                : 'N/A'
                            } 
                            book to ${
                              isChangeBookTypeModalConfirmation.newBookType
                                ? BookTypeIdToFriendlyName[
                                    isChangeBookTypeModalConfirmation.newBookType
                                  ]
                                : 'N/A'
                            } book). 
                            Please confirm your action.`}
            </Body1>
          }
          yesButtonText='Proceed'
          onYes={() => updateClient(isChangeBookTypeModalConfirmation.values as ClientDetailedDto)}
          onCancel={() =>
            setIsChangeBookTypeModalConfirmation({
              visible: false,
              oldBookType: undefined,
              newBookType: undefined,
              values: undefined,
            })
          }
          visible={isChangeBookTypeModalConfirmation.visible}
        />
      )}
      {isConfirmationActivateClientModalOpen && (
        <ConfirmationModal
          contentText='This action will activate the client. Do you wish to proceed?'
          yesButtonText='Proceed'
          onYes={verifyClient}
          onCancel={handleConfirmationActivateClientModalClose}
          visible={isConfirmationActivateClientModalOpen}
        />
      )}
      {downgradeRetailErrorModal && (
        <ErrorModal
          title='Error'
          renderBody={
            <>
              <span>
                Unable to downgrade client to retail classification as client has open positions
              </span>
            </>
          }
          onCancel={() => setDowngradeRetailErrorModal(false)}
          visible={downgradeRetailErrorModal}
        />
      )}
      {!!reassignIBModal && (
        <ConfirmationModal
          contentText={`Introducing broker is already linked to client's trading accounts. Do you still want to proceed?`}
          yesButtonText='Yes'
          onYes={() => updateClient(reassignIBModal, true)}
          onCancel={() => setReassignIBModal(undefined)}
          visible={!!reassignIBModal}
        />
      )}
      <ConfirmationModal
        contentText='This action will log out client from all devices. Do you wish to proceed?'
        yesButtonText='Yes, Proceed'
        cancelButtonText='Cancel'
        onYes={sendClientLogoutSession}
        onCancel={() => setForceLogoutModal(false)}
        visible={forceLogoutModal}
      />
      {!isFormLoading ? (
        <ClientDetailsDashboardForm
          show2FA={show2FA}
          showCreateLead={hasPermission(permissions, PermissionsEnum.CanCreateLeadForClient)}
          setWorldCheckModal={setWorldCheckModal}
          assignAffiliate={() => setAffiliateModal(true)}
          showForceLogoutModalConfirmation={() => setForceLogoutModal(true)}
          setMessage={setMessage}
          onWorldCheckResolve={onWorldCheckResolve}
          setTwoFactorModal={() => setTwoFactorModal(true)}
          createLead={createLead}
          data={data}
          permissions={permissions}
          clientType={clientType}
          gender={gender}
          nciType={nciType}
          education={education}
          teams={teams}
          documentType={documentType}
          businessNature={businessNature}
          language={language}
          classification={classification}
          marketDataClassification={marketDataClassification}
          landingPages={landingPages}
          bookType={bookType}
          accountLimits={accountLimits}
          countries={countries}
          users={users}
          truliooEidStatuses={truliooEidStatuses}
          truliooWatchlistStatuses={truliooWatchlistStatuses}
          truliooDocVStatuses={truliooDocVStatuses}
          tickmillCompanies={tickmillCompanies}
          worldCheckStatuses={worldCheckStatuses}
          salesStatuses={salesStatuses}
          displayCurrency={displayCurrency}
          preferredCurrency={preferredCurrency}
          introducingBrokersByCompany={introducingBrokersByCompany}
          complianceVulnerabilityGroups={complianceVulnerabilityGroups}
          getIntroducingBrokersByTickmillCompany={getIntroducingBrokersByTickmillCompany}
          isLoading={loading}
          dateFormatType={dateFormatType}
          leverageType={leverageType}
          userStatistics={userStatistics}
          tickmillProducts={tickmillProducts}
          amlCategories={amlCategories}
          tradingViewPlans={tradingViewPlans || []}
          tradingViewPlanExpirationDays={tradingViewPlanExpirationDays || []}
          onSubmit={onSubmit}
          updateClientStatus={updateClientStatus}
          updatePlatformLimits={updatePlatformLimits}
          resetFailedLogins={resetFailedLogins}
          resetForgotPasswordAttempts={resetForgotPasswordAttempts}
          setDocumentPreviewModal={setDocumentPreviewModal}
          setAccessLogModal={setAccessLogModal}
          setAmlDetailsModal={setAmlDetailsModal}
          setAuditLogModal={setAuditLogVisible}
          verifyClient={handleActivateClient}
          verifyClientModal={handleConfirmationActivateClientModalOpen}
          handleIdentityVerification={handleIdentityVerificationWindowOpen}
          setVerificationModal={setVerificationModal}
          setWorldCheckHistoryModal={setWorldCheckHistoryModal}
          setNCIHistoryModal={setNCIHistoryModal}
          taxIdAvailabilityStatus={taxAvailability}
          reCalculateNCI={reCalculateNCI}
          syncHubspot={syncHubspot}
          sendClientRequest={sendClientRequest}
          sendClientOtherRequest={sendClientOtherRequest}
          onCALogin={handleCALogin}
        />
      ) : (
        <Spinner />
      )}
    </React.Fragment>
  )
}

export default ClientDetailsDashboardPage
